import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { Product, ProductSize } from '@/mocks/products';

export interface CartItem {
  product: Product;
  size: ProductSize;
  quantity: number;
}

interface CartContextValue {
  items: CartItem[];
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (product: Product, size: ProductSize) => void;
  removeItem: (productId: number, sizeLabel: string) => void;
  updateQuantity: (productId: number, sizeLabel: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
}

const CartContext = createContext<CartContextValue | null>(null);

const STORAGE_KEY = 'pearl_cart_v1';
const EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface StoredCart {
  items: CartItem[];
  savedAt: number;
}

const loadCartFromStorage = (): CartItem[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: StoredCart = JSON.parse(raw);
    if (!parsed.savedAt || !Array.isArray(parsed.items)) return [];
    if (Date.now() - parsed.savedAt > EXPIRY_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return [];
    }
    return parsed.items;
  } catch {
    return [];
  }
};

const saveCartToStorage = (items: CartItem[]) => {
  try {
    const data: StoredCart = { items, savedAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // storage quota exceeded or unavailable — silently ignore
  }
};

const clearCartFromStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

/* ─── Toast component ─── */
const RestoredToast = ({ visible }: { visible: boolean }) => (
  <div
    className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] transition-all duration-500 ${
      visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
    }`}
    aria-live="polite"
  >
    <div className="flex items-center gap-2 bg-brand-olive text-white font-sans text-sm px-5 py-3 rounded-full shadow-lg whitespace-nowrap">
      <div className="w-4 h-4 flex items-center justify-center">
        <i className="ri-shopping-basket-2-line text-sm"></i>
      </div>
      Your cart is saved for later
    </div>
  </div>
);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() => loadCartFromStorage());
  const [isOpen, setIsOpen] = useState(false);
  const [showRestoredToast, setShowRestoredToast] = useState(false);

  // On mount: show toast if cart was restored with items
  useEffect(() => {
    const restored = loadCartFromStorage();
    if (restored.length > 0) {
      setShowRestoredToast(true);
      const timer = setTimeout(() => setShowRestoredToast(false), 3500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Persist cart to localStorage whenever items change
  useEffect(() => {
    if (items.length > 0) {
      saveCartToStorage(items);
    } else {
      clearCartFromStorage();
    }
  }, [items]);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const addItem = useCallback((product: Product, size: ProductSize) => {
    setItems((prev) => {
      const existing = prev.find(
        (i) => i.product.id === product.id && i.size.label === size.label
      );
      if (existing) {
        return prev.map((i) =>
          i.product.id === product.id && i.size.label === size.label
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      return [...prev, { product, size, quantity: 1 }];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((productId: number, sizeLabel: string) => {
    setItems((prev) =>
      prev.filter((i) => !(i.product.id === productId && i.size.label === sizeLabel))
    );
  }, []);

  const updateQuantity = useCallback(
    (productId: number, sizeLabel: string, quantity: number) => {
      if (quantity < 1) {
        setItems((prev) =>
          prev.filter((i) => !(i.product.id === productId && i.size.label === sizeLabel))
        );
        return;
      }
      setItems((prev) =>
        prev.map((i) =>
          i.product.id === productId && i.size.label === sizeLabel
            ? { ...i, quantity }
            : i
        )
      );
    },
    []
  );

  const clearCart = useCallback(() => {
    setItems([]);
    clearCartFromStorage();
  }, []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.size.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        isOpen,
        openCart,
        closeCart,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        totalItems,
        totalPrice,
      }}
    >
      {children}
      <RestoredToast visible={showRestoredToast} />
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextValue => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
